import { get } from 'lodash';
import clsx from 'clsx';
import isNumeric from 'validator/es/lib/isNumeric';
import isEmail from 'validator/es/lib/isEmail';
import isUUID from 'validator/es/lib/isUUID';
import isURL from 'validator/es/lib/isURL';
import urlDenyCharacters from '@/constants/validation';
import { linkPrefixes } from '@/constants/urls';
import {
  hasKey,
  stringsToCheck,
  safeArray,
  hasMinimumLength,
  isPlainObject,
  removeEmpty,
} from './resultsHelper';

export const isDuplicate = (key, value, items) => items.some((item) => item[key].toLowerCase() === value.toLowerCase());
export const alphaNumUnderscore = (str) => /^\w+$/.test(str);
export const startWithNumber = (str) => /^\d/.test(str);
export const invalidUrlCharacters = (str) => /[%?{}<>\\/]/.test(str);

export const indexOfError = (errors, entity, fields, matchAllFields = false) => (errors || []).findIndex(
  (error) => error.context.entity.includes(entity)
    && (!matchAllFields || fields.length === error.fields.length)
    && fields.every((v) => error.fields.includes(v)),
);

export const findError = (errors, entity, fields, matchAllFields = false) => {
  const index = indexOfError(errors, entity, fields, matchAllFields);
  return index >= 0 ? errors[index] : null;
};

export const validationClasses = (error, defaultClasses = undefined) => (
  clsx(defaultClasses, error && 'error aridhia-is-edit-mode--validation-error')
);

export const responseCode = ({ error }) => get(error, 'data.error.status') || error.status || null;

export const responseMessage = ({ error }) => get(error, 'data.error.message') || error.statusText || null;

const newError = (
  message,
  code = null,
  title = null,
  context = { entity: 'server' },
  fields = [],
  details = [],
  warning = false,
) => (
  {
    code,
    context,
    title,
    message,
    fields,
    details,
    warning,
    translation: {
      on: false,
      titleArgs: null,
      messageArgs: null,
      title: [],
      message: [],
      details: { on: false },
      links: false,
    },
  }
);

export const responseError = (
  action,
  title = null,
  context = { entity: 'server' },
  fields = [],
) => newError(responseMessage(action), responseCode(action), title, context, fields);

export const clientError = (context, title, message, fields) => newError(message, null, title, context, fields);

export const translatedError = (entity, title, message, fields = [], messageArgs = {}) => {
  const error = clientError(isPlainObject(entity) ? entity : { entity }, title, message, fields);
  error.translation.on = true;
  error.translation.messageArgs = messageArgs;

  return error;
};

export const checkIdentifier = (value, fields, errors, entity, title, identifier = true) => {
  if (identifier && stringsToCheck(value).some((item) => isNumeric(item) || invalidUrlCharacters(item))) {
    errors.push(translatedError(entity, title, 'validation.invalid.code', fields, { characters: urlDenyCharacters }));
  }
};

export const checkNumeric = (value, fields, errors, entity, title) => {
  if (stringsToCheck(value).some((item) => !isNumeric(item))) {
    errors.push(translatedError(entity, title, 'validation.numeric', fields));
  }
};

export const checkNonNumeric = (value, fields, errors, entity, title) => {
  if (stringsToCheck(value).some((item) => isNumeric(item))) {
    errors.push(translatedError(entity, title, 'validation.non.numeric', fields));
  }
};

export const checkExpression = (value, fields, errors, entity, title, expression, message) => {
  if (expression && stringsToCheck(value).some((item) => !expression.test(item))) {
    errors.push(translatedError(entity, title, message, fields));
  }
};

export const checkRequired = (
  value,
  fields,
  errors,
  entity,
  title,
  required = true,
  message = 'validation.required.inline',
) => {
  const items = safeArray(value);
  if (required && (items.length === 0 || items.some((item) => !hasMinimumLength(item)))) {
    errors.push(translatedError(entity, title, message, fields));
  }
};

export const checkMaxLength = (value, fields, errors, entity, title, maximum) => {
  if (maximum && stringsToCheck(value).some((item) => item.length > maximum)) {
    errors.push(translatedError(entity, title, 'validation.max.length', fields, { maximum }));
  }
};

export const checkMinLength = (value, fields, errors, entity, title, minimum) => {
  if (minimum && stringsToCheck(value).some((item) => item.length < minimum)) {
    errors.push(translatedError(entity, title, 'validation.min.length', fields, { minimum }));
  }
};

export const checkMaxNumeric = (value, fields, errors, entity, title, maximum) => {
  if (maximum && stringsToCheck(value).some((item) => isNumeric(item) && item > maximum)) {
    errors.push(translatedError(entity, title, 'validation.max.number', fields, { maximum }));
  }
};

export const checkMinNumeric = (value, fields, errors, entity, title, minimum) => {
  if (minimum && stringsToCheck(value).some((item) => isNumeric(item) && item < minimum)) {
    errors.push(translatedError(entity, title, 'validation.min.number', fields, { minimum }));
  }
};

const invalidUrl = (url) => (
  url.length > 0 && (!isURL(url) || !Object.values(linkPrefixes).some((prefix) => url.startsWith(prefix)))
);

export const checkValidUrl = (value, fields, errors, entity, title, message = 'validation.url.not.valid') => {
  if (stringsToCheck(value).map((url) => url.toLowerCase()).some(invalidUrl)) {
    errors.push(translatedError(entity, title, message, fields, { value }));
  }
};

export const checkValidEmail = (value, fields, errors, entity, title, message = 'validation.email.not.valid') => {
  if (stringsToCheck(value).some((email) => email.length > 0 && !isEmail(email))) {
    errors.push(translatedError(entity, title, message, fields, { value }));
  }
};

export const checkValidUuid = (value, fields, errors, entity, title, message = 'validation.uuid.not.valid') => {
  if (stringsToCheck(value).some((uuid) => uuid.length > 0 && !isUUID(uuid))) {
    errors.push(translatedError(entity, title, message, fields, { value }));
  }
};

// Regex patterns for IP address and IP CIDR range
// e.g. 192.168.0.1
//      192.168.0.0/24
const v4RegexPattern = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)(?:\\.(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)){3}';
const v4CidrRegexPattern = `${v4RegexPattern}\\/(3[0-2]|[12]?[0-9])`;

export const isValidIp4 = (ip) => {
  const v4Exact = new RegExp(`^${v4RegexPattern}$`);
  return v4Exact.test(ip);
};

export const isValidIp4Cidr = (ip) => {
  const v4CidrExact = new RegExp(`^${v4CidrRegexPattern}$`);
  return v4CidrExact.test(ip);
};

export const validateIpRange = (value) => {
  let valid = true;
  if (value) {
    const values = value.split(',');
    values.forEach((val) => {
      if (!isValidIp4(val.trim()) && !isValidIp4Cidr(val.trim())) {
        valid = false;
      }
    });
  }

  return valid;
};

export const clientErrors = (errors) => errors.filter((e) => (e.context.entity !== 'server'));

export const responseErrors = (errors) => errors.filter((e) => (e.context.entity === 'server'));

export const hasErrorCodes = (errors, codes) => (errors || []).some(({ code }) => (codes.includes(code)));

export const hasErrorCode = (errors, code) => hasErrorCodes(errors, [code]);

export const firstResponseError = (errors) => (errors || []).find(({ context }) => context.entity === 'server');

export const firstClientError = (errors) => (errors || []).find(({ context }) => context.entity !== 'server');

export const firstClientErrorMessage = (errors) => firstClientError(errors)?.message || '';

export const firstError = (errors) => (errors ? (errors[0] || null) : null);

export const firstErrorMessage = (errors) => firstError(errors)?.message || '';

export const pruneServerErrors = (errors) => errors.filter((e) => (e.context.entity !== 'server'));

export const pruneFieldError = (error, field) => (field ? !error.fields.includes(field) : false);

const customPrune = (error, contextCb, defaultValue = false) => (contextCb ? contextCb(error) : defaultValue);

export const pruneFieldErrors = (errors, field, contextCb = null) => (
  errors.filter((e) => (pruneFieldError(e, field) && customPrune(e, contextCb, true)))
);

export const pruneMatchingFieldErrors = (errors, { properties }, fields) => (
  fields.reduce((acc, { name, field, override }) => (
    (override || hasKey(properties, name)) ? pruneFieldErrors(acc, field) : acc
  ), errors)
);

const errorWithEntities = (error, entities) => entities.some((entity) => error.context.entity.includes(entity));

export const errorsWithEntities = (errors, entities) => (
  removeEmpty((errors || []).map((e) => (errorWithEntities(e, entities) ? e : null)))
);

export const pruneEntityErrors = (errors, entities, contextCb = null) => (
  errors.filter((e) => ((errorWithEntities(e, entities)) ? customPrune(e, contextCb) : true))
);

export const pruneEntityFieldErrors = (errors, field, entities) => (
  pruneFieldErrors(errors, field, (e) => errorWithEntities(e, entities))
);

export const errorsWithoutContextAndFields = (errors, { context, fields }) => (
  errors.filter((e) => e.context !== context && e.fields !== fields)
);

export const errorKey = (error, contexts = [], prefix = [], suffix = []) => (
  removeEmpty([...prefix,
    error.context.entity,
    ...(contexts || []).map((c) => error.context[c]),
    ...(error.fields || []),
    ...suffix]).join('_')
);

export const errorTitle = (error, intl) => (
  error.translation?.on
    ? intl.formatMessage({ id: error.title }, error.translation.titleArgs || {})
    : error.title
);

export const errorMessage = (error, intl) => (
  error.translation?.on && hasMinimumLength(error.message)
    ? intl.formatMessage({ id: error.message }, error.translation.messageArgs || {})
    : error.message
);

export const errorHasLinks = (error) => error.translation?.on && error.translation?.links;

export const errorDetails = (error, intl) => {
  const details = error?.details || [];

  if (error.translation?.details?.on && details?.length > 0) {
    details.forEach((detail, index) => {
      details[index] = intl.formatMessage({ id: detail });
    });
  }

  return details;
};

export default newError;
